import { createToken, Lexer, extendToken } from 'chevrotain';

export const LexerModes = {
  NORMAL: 'normal',
  JINJA_EXPR: 'jinja_expr',
  JINJA_STMT: 'jinja_stmt',
  JINJA_COMMENT: 'jinja_comment',
};

// --- Section: Mode transition tokens ---
export const JinjaExprStart = createToken({ name: 'JinjaExprStart', pattern: /\{\{/, push_mode: LexerModes.JINJA_EXPR });
export const JinjaExprEnd = createToken({ name: 'JinjaExprEnd', pattern: /\}\}/, pop_mode: true });

// Combined statement start tokens
export const JinjaIfStart = createToken({ name: 'JinjaIfStart', pattern: /\{%\s*if\b/, push_mode: LexerModes.JINJA_STMT });
export const JinjaElifStart = createToken({ name: 'JinjaElifStart', pattern: /\{%\s*elif\b/, push_mode: LexerModes.JINJA_STMT });
export const JinjaElseStart = createToken({ name: 'JinjaElseStart', pattern: /\{%\s*else\b/, push_mode: LexerModes.JINJA_STMT });
export const JinjaEndifStart = createToken({ name: 'JinjaEndifStart', pattern: /\{%\s*endif\b/, push_mode: LexerModes.JINJA_STMT });

export const JinjaForStart = createToken({ name: 'JinjaForStart', pattern: /\{%\s*for\b/, push_mode: LexerModes.JINJA_STMT });
export const JinjaEndforStart = createToken({ name: 'JinjaEndforStart', pattern: /\{%\s*endfor\b/, push_mode: LexerModes.JINJA_STMT });

export const JinjaSetStart = createToken({ name: 'JinjaSetStart', pattern: /\{%\s*set\b/, push_mode: LexerModes.JINJA_STMT });
export const JinjaBlockStart = createToken({ name: 'JinjaBlockStart', pattern: /\{%\s*block\b/, push_mode: LexerModes.JINJA_STMT });
export const JinjaEndblockStart = createToken({ name: 'JinjaEndblockStart', pattern: /\{%\s*endblock\b/, push_mode: LexerModes.JINJA_STMT });

export const JinjaExtendsStart = createToken({ name: 'JinjaExtendsStart', pattern: /\{%\s*extends\b/, push_mode: LexerModes.JINJA_STMT });
export const JinjaIncludeStart = createToken({ name: 'JinjaIncludeStart', pattern: /\{%\s*include\b/, push_mode: LexerModes.JINJA_STMT });
export const JinjaMacroStart = createToken({ name: 'JinjaMacroStart', pattern: /\{%\s*macro\b/, push_mode: LexerModes.JINJA_STMT });
export const JinjaEndmacroStart = createToken({ name: 'JinjaEndmacroStart', pattern: /\{%\s*endmacro\b/, push_mode: LexerModes.JINJA_STMT });

export const JinjaStmtEnd = createToken({ name: 'JinjaStmtEnd', pattern: /%\}/, pop_mode: true });

export const JinjaCommentStart = createToken({ name: 'JinjaCommentStart', pattern: /\{#/, push_mode: LexerModes.JINJA_COMMENT });
export const JinjaCommentEnd = createToken({ name: 'JinjaCommentEnd', pattern: /#\}/, pop_mode: true });

// --- Section: Other tokens ---
export const JinjaIdentifier = createToken({ name: 'JinjaIdentifier', pattern: /\b[a-zA-Z_][a-zA-Z0-9_]*\b/ });

export const JinjaStringLiteral = createToken({
  name: 'JinjaStringLiteral',
  pattern: /"([^"\\]*(\\.[^"\\]*)*)"|'([^'\\]*(\\.[^'\\]*)*)'/
});

export const JinjaNumberLiteral = createToken({
  name: 'JinjaNumberLiteral',
  pattern: /-?\d+(\.\d+)?([eE][+-]?\d+)?\b/
});

export const JinjaOperator = createToken({
  name: 'JinjaOperator',
  pattern: /\b(?:and|or|not)\b|==|!=|<=|>=|<|>|\+|-|\*|\/|%|\|\|?|&&?/
});

export const JinjaPunctuation = createToken({
  name: 'JinjaPunctuation',
  pattern: /[\(\)\[\]\{\},.:|]/
});

export const JinjaIn = createToken({ name: 'JinjaIn', pattern: /\bin\b/ });

// --- Section: Chunks and whitespace ---
export const JinjaExprChunk = createToken({
  name: 'JinjaExprChunk',
  pattern: /[^}]+/,
  line_breaks: true
});

export const JinjaStmtChunk = createToken({
  name: 'JinjaStmtChunk',
  pattern: /[^%]+/,
  line_breaks: true
});

export const JinjaCommentChunk = createToken({
  name: 'JinjaCommentChunk',
  pattern: /[^#]+/,
  line_breaks: true,
  group: Lexer.SKIPPED
});

export const WhiteSpace = createToken({
  name: 'WhiteSpace',
  pattern: /[ \t\r\n]+/,
  group: Lexer.SKIPPED
});

// --- Section: Token export ---
export const allJinjaTokens = [
  WhiteSpace,
  JinjaStmtEnd,
  JinjaExprStart, JinjaExprEnd,
  JinjaCommentStart, JinjaCommentEnd,

  // Combined statement start tokens
  JinjaIfStart, JinjaElifStart, JinjaElseStart, JinjaEndifStart,
  JinjaForStart, JinjaEndforStart,
  JinjaSetStart, JinjaBlockStart, JinjaEndblockStart,
  JinjaExtendsStart, JinjaIncludeStart, JinjaMacroStart, JinjaEndmacroStart,

  // Literals and symbols
  JinjaStringLiteral,
  JinjaNumberLiteral,
  JinjaOperator,
  JinjaPunctuation,
  JinjaIdentifier,
  JinjaIn,

  // Chunks
  JinjaStmtChunk,
  JinjaExprChunk,
];

export const JinjaLexer = new Lexer({
  modes: {
    [LexerModes.NORMAL]: [
      WhiteSpace,
      JinjaExprStart,
      JinjaIfStart, JinjaElifStart, JinjaElseStart, JinjaEndifStart,
      JinjaForStart, JinjaEndforStart,
      JinjaSetStart, JinjaBlockStart, JinjaEndblockStart,
      JinjaExtendsStart, JinjaIncludeStart, JinjaMacroStart, JinjaEndmacroStart,
      JinjaCommentStart,
    ],
    [LexerModes.JINJA_EXPR]: [
      WhiteSpace,
      JinjaStringLiteral,
      JinjaNumberLiteral,
      JinjaOperator,
      JinjaPunctuation,
      JinjaIdentifier,
      JinjaIn,
      JinjaExprChunk,
      JinjaExprEnd,
    ],
    [LexerModes.JINJA_STMT]: [
      WhiteSpace,
      JinjaStringLiteral,
      JinjaNumberLiteral,
      JinjaOperator,
      JinjaPunctuation,
      JinjaIdentifier,
      JinjaIn,
      JinjaStmtChunk,
      JinjaStmtEnd,
    ],
    [LexerModes.JINJA_COMMENT]: [
      JinjaCommentChunk,
      JinjaCommentEnd,
    ],
  },
  defaultMode: LexerModes.NORMAL,
});
